import { Component, HostBinding, OnDestroy, Renderer2, ViewChild } from '@angular/core';
import { faCircleCheck, faCircleXmark } from '@fortawesome/pro-solid-svg-icons';
import { ToastrService } from 'ngx-toastr';
import { COLUMNS_HEADER_VIEW_APO, COLUMNS_HEADER_VIEW_PROVISIONAL } from '../../core/constants/tables.constants';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { GenericTableComponent } from '../../components/generic-table/generic-table.component';
import { PlanningOrdersTableComponent } from '../../components/planning-orders-table/planning-orders-table.component';
import { SpinnerComponent } from '../../components/spinner/spinner.component';
import { FileUploadComponent } from '../../components/file-upload/file-upload.component';
import { UploadApoStepsComponent } from '../../components/upload-apo-steps/upload-apo-steps.component';
import { UploadApoService } from '../../core/services/upload-apo.service';
import { ApoFineTunningComponent } from '../../components/apo-fine-tunning/apo-fine-tunning.component';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { ApoParametersComponent } from '../../components/apo-parameters/apo-parameters.component';
import { ValueStream } from '../../core/enums/general.enum';
import { CameraHeatmap } from '../../core/interfaces/camera-heatmap.interface';
import { BlendersService } from '../../core/services/blenders.service';
import { PackagingMachinesService } from '../../core/services/packaging-machines.service';
import { forkJoin } from 'rxjs';

@Component({
  selector: 'app-upload-apo',
  standalone: true,
  imports: [FontAwesomeModule, GenericTableComponent, PlanningOrdersTableComponent, SpinnerComponent, FileUploadComponent, UploadApoStepsComponent, ApoFineTunningComponent, CommonModule, ApoParametersComponent],
  templateUrl: './upload-apo.component.html',
  styleUrl: './upload-apo.component.scss'
})
export class UploadApoComponent implements OnDestroy {
  @ViewChild(ApoParametersComponent) apoParametersComponent?: ApoParametersComponent;

  @HostBinding('class.vs2-theme')
  get isVs2Theme() { return this._selectedVS === ValueStream.VS2; }

  COLUMNS_HEADER = COLUMNS_HEADER_VIEW_PROVISIONAL;

  faActiveCheck = faCircleCheck;
  faCloseCheck = faCircleXmark;

  ValueStream = ValueStream;
  apoFile: any;
  selectedTab: string = 'upload';
  apoUpload: Record<string, any[]> | null = null;
  apoLogistics: any;
  recalculatedApoLogistics: any = undefined;
  isModalVisible = false;
  isLoading = false;
  formParameters: any;
  /** Set via app-apo-parameters (formValidityChange); avoids NG0100 from ViewChild + [disabled]. */
  delaysFormValid = false;

  blenderOptions: any[] = [];
  packagingMachineOptions: any[] = [];
  demandTypeOptions = [
    { id: 'MTA', description: 'MTA' },
    { id: 'MTO', description: 'MTO' },
    { id: 'MTS', description: 'MTS' },
  ];

  get currentVSOrders(): any[] {
    const all = this.apoUpload?.[this.selectedVS] ?? [];
    return all.filter((o: any) => o.value_stream === this.selectedVS);
  }

  private _selectedVS = ValueStream.VS1;
  get selectedVS() { return this._selectedVS; }
  set selectedVS(vs: ValueStream) {
    this._selectedVS = vs;
    const add = vs === ValueStream.VS2;
    const method = add ? 'addClass' : 'removeClass';
    this.renderer[method](document.body, 'vs2-theme');
    this.renderer[method](document.documentElement, 'vs2-theme');
  }

  steps = [{
    name: 'upload',
    description: '1. Importar',
    completed: false,
    active: true
  },
  {
    name: 'view',
    description: '2. Visualizar',
    completed: false,
    active: false
  },
  {
    name: 'delays',
    description: '3. Retrasos & OEE',
    completed: false,
    active: false
  },
  {
    name: 'validate',
    description: '4. Validación',
    completed: false,
    active: false
  }];
  calendar: any;
  cameraHeatmap: CameraHeatmap | null = null;

  constructor(
    private uploadApoService: UploadApoService,
    private toastr: ToastrService,
    private router: Router,
    private renderer: Renderer2,
    private blendersService: BlendersService,
    private packagingMachinesService: PackagingMachinesService,
  ) { }

  ngOnInit() {
    this.isLoading = true;
    this.apoUpload = null;
    forkJoin({
      blenders: this.blendersService.getBlenders(),
      packagingMachines: this.packagingMachinesService.getPackagingMachines(),
    }).subscribe({
      next: ({ blenders, packagingMachines }) => {
        this.blenderOptions = blenders as any[];
        this.packagingMachineOptions = packagingMachines as any[];
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
      }
    });
  }

  ngOnDestroy() {
    this.renderer.removeClass(document.body, 'vs2-theme');
    this.renderer.removeClass(document.documentElement, 'vs2-theme');
  }

  onUploadFile(file: any) {
    this.isLoading = true;
    this.uploadApoService.uploadApoFile(file).subscribe(res => {
      this.apoUpload = res.apoUpload;
      for (let warning of res.warnings) {
        this.toastr.warning('',warning,{
          positionClass: 'toast-bottom-right',
          timeOut: 60000,
          closeButton: true,
          tapToDismiss: false, 
          disableTimeOut:'extendedTimeOut'
        });
      }
      this.steps[0].completed = true;
      this.steps[0].active = false;
      this.steps[1].active = true;
      this.selectedTab = this.steps[1].name;
      this.isLoading = false;
      this.toastr.success(`${res.totalOrders} órdenes cargadas`, `Carga del fichero APO completada`);
    }, error => {
      this.toastr.error('', error?.error?.message ?? 'Error desconocido', {
        positionClass: 'toast-bottom-right',
        timeOut: 60000,
        closeButton: true,
        tapToDismiss: false, 
        disableTimeOut:'extendedTimeOut'
      });
      this.isLoading = false;
    });
  }

  onSelectTab(tab: any) {
    if (tab.name === 'view' && this.selectedTab !== 'view' && this.apoUpload) {
      this.isLoading = true;
      this.uploadApoService.getApoUpload().subscribe({
        next: (res) => {
          this.apoUpload = res.apoUpload;
          this.isLoading = false;
        },
        error: () => {
          this.isLoading = false;
        }
      });
    }
    this.selectedTab = tab.name;
    if (tab.name !== 'delays') {
      this.delaysFormValid = false;
    }
  }

  onOrdersChange(updatedOrders: any[]): void {
    if (!this.apoUpload) return;
    this.apoUpload = {
      ...this.apoUpload,
      [this.selectedVS]: updatedOrders
    };
  }

  onDelaysFormValidityChange(valid: boolean): void {
    this.delaysFormValid = valid;
  }

  nextStep() {
    const stepActive = this.steps.find(step => step.active);
    if (stepActive?.name === 'view') {
      const allOrders = Object.values(this.apoUpload ?? {}).flat();
      this.isLoading = true;
      this.uploadApoService.updateApoUpload(allOrders).subscribe({
        next: (res: any) => {
          this.apoUpload = res.apoUpload;
          this.isLoading = false;
          this.steps[1].completed = true;
          this.steps[1].active = false;
          this.steps[2].active = true;
          this.selectedTab = this.steps[2].name;
        },
        error: (err) => {
          this.isLoading = false;
          this.toastr.error('', err?.error?.message ?? 'Error al guardar los cambios', {
            positionClass: 'toast-bottom-right',
            timeOut: 10000,
            closeButton: true
          });
        }
      });
    } else if (stepActive?.name === 'delays') {
      this.steps[2].completed = true;
      this.steps[2].active = false;
      this.steps[3].active = true;
      this.selectedTab = this.steps[3].name;
      if (this.apoParametersComponent) {
        const raw = this.apoParametersComponent.parametersFormGroup.value;
        const payload: Record<string, any> = { replanificar: false };
        for (const vs of Object.keys(raw)) {
          const allItems = [...raw[vs].blendersForm, ...raw[vs].packagingMachinesForm, ...raw[vs].qualityForm];
          const formValues = allItems.reduce((acc: any, item: any) => ({ ...acc, ...item }), {});
          payload[vs] = {
            formValues,
            replanificar: false,
            orders: this.apoUpload?.[vs] ?? []
          };
        }
        this.formParameters = payload;
        this.getApoLogistics(payload);
      }
    }
  }

  getApoLogistics(formValues: any) {
    this.isLoading = true;
    this.uploadApoService.getApoLogistics(formValues).subscribe((res: any ) => {
      this.apoLogistics = res.orders;
      this.calendar = res.calendar;
      this.cameraHeatmap = res.camera_heatmap ?? null;
      this.isLoading = false;
    });
  }

  confirm() {
    const allOrders = Object.values(this.apoLogistics).flat();
    this.uploadApoService.apoLogisticsConfirm(allOrders).subscribe(res => {
      this.router.navigate(['/']);
    });
  }

  onApoLogisticsChange(apoLogistics: any) {
    this.recalculatedApoLogistics = {
      ...this.apoLogistics,
      [this.selectedVS]: apoLogistics
    };
  }

  recalculateApoLogistics() {
    this.isLoading = true;
    this.isModalVisible = false;
    this.uploadApoService.recalculateApoLogistics(this.formParameters , this.recalculatedApoLogistics).subscribe((res: any) => {
      this.apoLogistics = res.orders;
      this.calendar = res.calendar;
      this.cameraHeatmap = res.camera_heatmap ?? null;
      this.recalculatedApoLogistics = undefined;
      this.isLoading = false;
    });
  }

}
